import * as Yup from "yup";
import { IFirmaRelationerType } from "../../../components/pages/firma/id/Stamoplysninger/FirmaRelationer";
import { dateIsWithinInterval, dateToyyyyMMdd } from "../../../util/date";
import { getPatchOperations } from "../../../util/form";

export const existingRelationTest = (
  parent,
  selected: string | undefined,
  firmaRelationer: IFirmaRelationerType[],
  firmaejerskabId: string | undefined,
): boolean => {
  const existingRelations = firmaRelationer.filter(
    (relation) => relation.firmaId === selected,
  );
  if (existingRelations.length === 0) {
    return true;
  } else {
    for (const existingRelation of existingRelations) {
      if (existingRelation.id === firmaejerskabId) return true;
      const period = existingRelation.periode;
      const selectedPeriod: {
        fraDato: string | undefined;
        tilDatoInklusiv: string | undefined;
      } = {
        fraDato: parent["periodeFraDato"],
        tilDatoInklusiv: parent["periodeTilDatoInklusiv"],
      };
      if (!selectedPeriod.fraDato) return true;

      const existingPeriodeFraDato = new Date(period.fraDato);
      const existingPeriodeTilDatoInklusiv = new Date(period.tilDatoInklusiv);

      const selectedPeriodeFraDato = new Date(selectedPeriod.fraDato);
      const selectedPeriodeTilDatoInklusiv = new Date(
        selectedPeriod.tilDatoInklusiv ?? "9999-12-31",
      );

      // Checks if the selected period overlaps with any existing periods
      if (
        dateIsWithinInterval(
          selectedPeriodeFraDato,
          existingPeriodeFraDato,
          existingPeriodeTilDatoInklusiv,
        )
      ) {
        return false;
      }
      if (
        dateIsWithinInterval(
          selectedPeriodeTilDatoInklusiv,
          existingPeriodeFraDato,
          existingPeriodeTilDatoInklusiv,
        )
      ) {
        return false;
      }

      // Checks if the existing period overlaps with the selected period
      if (
        dateIsWithinInterval(
          existingPeriodeFraDato,
          selectedPeriodeFraDato,
          selectedPeriodeTilDatoInklusiv,
        )
      ) {
        return false;
      }
      if (
        dateIsWithinInterval(
          existingPeriodeTilDatoInklusiv,
          selectedPeriodeFraDato,
          selectedPeriodeTilDatoInklusiv,
        )
      ) {
        return false;
      }
    }
    return true;
  }
};

export interface FirmaRelationer {
  firma: {
    firmaejerskabEjetFirma: { ejendeFirmaId: string; id: string }[];
    firmaejerskabEjendeFirmaNavigation: { ejetFirmaId: string; id: string }[];
  };
}

export const firmaejerskabDelete = (
  firmaId: string,
  firmaejerskabId: string,
) => {
  return {
    query: `firma/${firmaId}/ejerskaber/${firmaejerskabId}`,
  };
};
export const firmaejerskabEjetAfForm = (
  firmaId: string,
  firmaRelationer: IFirmaRelationerType[],
  firmaejerskabId?: string,
): FormConfig<FirmaejerskabDTO> => {
  return {
    title: "Ejet Af",
    lockedValues: [
      { id: "firmaId", value: firmaId },
      {
        id: "firmaejerskabId",
        value: firmaejerskabId,
      },
    ],
    validationSchema: Yup.object({
      ejendeFirmaId: Yup.string()
        .test(
          "overlappingRelationCheck",
          "Dette firma har allerede samme relation indenfor samme periode",
          function (selected) {
            return existingRelationTest(
              this.parent,
              selected,
              firmaRelationer,
              firmaejerskabId,
            );
          },
        )
        .required("Firma skal være valgt"),
      periodeFraDato: Yup.date().nullable().required("Startdato skal være sat"),
      periodeTilDatoInklusiv: Yup.date()
        .nullable()
        .when(
          "periodeFraDato",
          (periodeFraDato, yup) =>
            periodeFraDato &&
            yup.min(periodeFraDato, "Slutdato kan ikke være inden startdato"),
        ),
    }),
    form: [
      {
        component: "firma",
        id: "ejendeFirmaId",
        label: "Vælg firma",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeFraDato",
        label: "Startdato",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeTilDatoInklusiv",
        label: "Slutdato",
        initialValue: "",
      },
    ],
    get: `firma/${firmaId}/ejerskaber/${firmaejerskabId}`,
    post(values: FirmaejerskabDTO) {
      const { ejendeFirmaId, periodeFraDato, periodeTilDatoInklusiv } = values;
      return {
        query: `firma/${firmaId}/ejerskaber`,
        body: {
          ejendeFirmaId,
          periodeFraDato: dateToyyyyMMdd(periodeFraDato),
          periodeTilDatoInklusiv: periodeTilDatoInklusiv
            ? dateToyyyyMMdd(periodeTilDatoInklusiv)
            : "9999-12-31",
        },
      };
    },
    patch: (values: FirmaejerskabDTO) => {
      const ops = getPatchOperations(values);

      return {
        query: `firma/${firmaId}/ejerskaber/${firmaejerskabId}`,
        body: JSON.stringify(ops),
      };
    },
    delete: () => firmaejerskabDelete(firmaId, firmaejerskabId ?? ""),
  };
};
